import { useMemo } from 'react';
import { clsx } from 'clsx';
import { InterchangeLogomark } from '../../icons/logo/mark/interchange-logomark';
import { ImageLoadingStatus, useImageLoader } from '../../util/image';
import { VerticalStack } from '../alignment/vertical-stack';
import { Skeleton } from '../loading/skeleton';
import styles from './logomark.module.scss';

export type LogomarkSize = 'small' | 'medium' | 'large';

type LogomarkVariant = 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'rainbow' | 'rainbow-icon' | 'image';

const INTERCHANGE_LOGO_SIZE_BY_LOGOMARK_SIZE: Record<LogomarkSize, number> = {
  small: 10,
  medium: 15,
  large: 20,
};

function getVariant(tenantType: string, fileSrc: string | undefined, imageStatus: ImageLoadingStatus): LogomarkVariant {
  if (fileSrc && imageStatus !== ImageLoadingStatus.Error) {
    return 'image';
  }

  const tenantTypeToLower = tenantType.toLowerCase();

  switch (tenantTypeToLower) {
    case 'platform':
      return 'rainbow';
    case 'clearing':
      return 'rainbow-icon';
    case 'program':
      return 'secondary';
    case 'o5':
      return 'quaternary';
    case 'organization':
    default:
      return 'primary';
  }
}

interface LogomarkProps {
  className?: string;
  fileSrc?: string;
  size?: LogomarkSize;
  tenantName: string | undefined;
  tenantType: string | undefined;
}

export function Logomark({ className, fileSrc, size = 'medium', tenantName, tenantType }: LogomarkProps) {
  const imageStatus = useImageLoader(fileSrc);
  const { showLoading, variant } = useMemo(() => {
    if (!tenantType) {
      return {
        showLoading: true,
        variant: 'primary',
      };
    }

    const showVariant = getVariant(tenantType, fileSrc, imageStatus);

    return {
      showLoading: imageStatus === ImageLoadingStatus.Loading,
      variant: showVariant,
    };
  }, [imageStatus, fileSrc, tenantType]);

  const baseClassName = clsx(styles.logomark, styles[`size-${size}`], className);

  if (showLoading) {
    return <Skeleton parentClassName={baseClassName} type="rect" />;
  }

  let content = <span>{(tenantName?.charAt(0) || tenantType?.charAt(0))?.toUpperCase()}</span>;

  if (variant === 'image') {
    content = <img alt={`${tenantName || tenantType} logomark`} src={fileSrc} />;
  } else if (tenantType?.toLowerCase() === 'clearing') {
    content = <InterchangeLogomark className={styles.interchangeLogomark} height={INTERCHANGE_LOGO_SIZE_BY_LOGOMARK_SIZE[size]} />;
  } else if (tenantType?.toLowerCase() === 'platform') {
    content = (
      <InterchangeLogomark
        className={clsx(styles.interchangeLogomark, styles.white)}
        fill="currentColor"
        height={INTERCHANGE_LOGO_SIZE_BY_LOGOMARK_SIZE[size]}
      />
    );
  }

  return (
    <VerticalStack align="center" className={clsx(baseClassName, styles[`variant-${variant}`])} inlineAlign="center">
      {content}
    </VerticalStack>
  );
}
